import { api as Api } from 'api/http'

// Waits between polls: quick at first, since most text generations finish in a few seconds,
// then backing off. A parallel batch of generations each polls, and the API allows 200
// requests a minute per user, so the cap keeps a batch well inside that.
export const POLL_DELAYS_MS = [700, 1500, 1500, 3000, 3000, 5000]
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000
// A poll that fails on the network is retried; this many failures in a row give up.
const MAX_CONSECUTIVE_POLL_ERRORS = 3

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Polls GET /aiResults/{id} until the generation finishes.
 *
 * Resolves with the ai_result (`{ id, status, response, ... }`) once it is completed.
 * Rejects with an Error carrying the row's status_message when it failed, or when it is
 * still running after `timeoutMs`.
 */
export const pollAiResult = async (id, { timeoutMs = DEFAULT_TIMEOUT_MS, delays = POLL_DELAYS_MS } = {}) => {
    const deadline = Date.now() + timeoutMs
    let attempt = 0
    let consecutiveErrors = 0

    while (Date.now() < deadline) {
        await sleep(delays[Math.min(attempt, delays.length - 1)])
        attempt++

        let result
        try {
            result = (await Api.get(`/aiResults/${id}`))?.data
            consecutiveErrors = 0
        } catch (err) {
            consecutiveErrors++
            if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
                throw err
            }
            continue
        }

        if (result?.status === 'completed') {
            return result
        }
        if (result?.status === 'failed') {
            throw new Error(result?.status_message || 'The generation failed. Please try again.')
        }
    }

    throw new Error('The generation is taking longer than expected. Please try again.')
}

export default pollAiResult
